import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import styles from './flipBook.module.scss';
import { FlipViewportAnimationSuppressContext } from './flipViewportAnimationSuppressContext';
import { FlipSlotContext } from './flipSlotContext';

const FLIP_TOTAL_MS  = 550; // css: 0.22s delay + 0.25s fold = 0.47s + 80ms headroom
const SLIDE_TOTAL_MS = 450;
const STAGE_SETTLE_MS = 60;
const STAGE_MAX_MS = 200;
export { FlipSlotContext };

// ── Fold slot class helper (desktop) ─────────────────────────────────────────
function foldSlotClass(slot, direction) {
  const map = {
    next: {
      currentA:  styles.flipNextCurrentA,
      currentB:  styles.flipNextCurrentB,
      upcomingA: styles.flipNextUpcomingA,
      upcomingB: styles.flipNextUpcomingB,
    },
    prev: {
      currentA:  styles.flipPrevCurrentA,
      currentB:  styles.flipPrevCurrentB,
      upcomingA: styles.flipPrevUpcomingA,
      upcomingB: styles.flipPrevUpcomingB,
    },
  };
  return map[direction]?.[slot] ?? '';
}

// ── Slide slot class helper (mobile) ─────────────────────────────────────────
function slideSlotClass(role, direction) {
  const map = {
    next: { current: styles.slideCurrentNext, upcoming: styles.slideUpcomingNext },
    prev: { current: styles.slideCurrentPrev, upcoming: styles.slideUpcomingPrev },
  };
  return map[direction]?.[role] ?? '';
}


export const FlipbookFlipContainer = ({
  currentContent,
  upcomingContent,
  direction,       // 'next' | 'prev' | null
  onAnimationEnd,
  mobile = false,
}) => {
  const timerRef       = useRef(null);
  const viewportRef    = useRef(null);
  const onAnimationEndRef = useRef(onAnimationEnd);
  onAnimationEndRef.current = onAnimationEnd;

  // false while slots are staged (hidden); true once the fold/slide animations run.
  const [armed, setArmed] = useState(false);
  // false while the base slot still holds the current page; true once it holds
  // the upcoming page (one frame after arming, invisible behind the fold).
  const [baseSwapped, setBaseSwapped] = useState(false);

  useEffect(() => {
    if (!direction) {
      setArmed(false);
      setBaseSwapped(false);
      return;
    }

    // Everything scheduled during staging, torn down together if the flip is
    // cancelled (or re-triggered) before it arms.
    const cleanups = [];
    const onRaf = (fn) => {
      const id = requestAnimationFrame(fn);
      cleanups.push(() => cancelAnimationFrame(id));
    };
    const onTimer = (fn, ms) => {
      const id = setTimeout(fn, ms);
      cleanups.push(() => clearTimeout(id));
    };

    let done = false;
    const arm = () => {
      if (done) return;
      done = true;
      setArmed(true);
    };

    // Frame 1 commits + paints the staged slots; then wait for their images.
    onRaf(() => {
      const pending = viewportRef.current
        ? Array.from(viewportRef.current.querySelectorAll('img')).filter(
          (img) => !img.complete,
        )
        : [];

      // Cap staging regardless of what the images do.
      onTimer(arm, STAGE_MAX_MS);

      if (!pending.length) {
        onTimer(arm, STAGE_SETTLE_MS);
        return;
      }

      let settled = 0;
      const onSettled = () => {
        settled += 1;
        // One more frame so the decoded image actually paints before the reveal.
        if (settled >= pending.length) onRaf(arm);
      };
      pending.forEach((img) => {
        img.addEventListener('load', onSettled, { once: true });
        img.addEventListener('error', onSettled, { once: true });
        cleanups.push(() => {
          img.removeEventListener('load', onSettled);
          img.removeEventListener('error', onSettled);
        });
      });
    });

    return () => {
      done = true;
      cleanups.forEach((fn) => fn());
      setArmed(false);
      setBaseSwapped(false);
    };
  }, [direction]);

  // Base content swap runs one frame after arming — the base is hidden by then,
  // so the upcoming page's mount cost lands mid-fold, never on the arm frame.
  useEffect(() => {
    if (!armed) return;
    const raf = requestAnimationFrame(() => setBaseSwapped(true));
    return () => cancelAnimationFrame(raf);
  }, [armed]);

  // Completion timer runs from ARMING (when the CSS animations actually start),
  // not from `direction` — otherwise slow staging would eat into the animation.
  useEffect(() => {
    if (!direction || !armed) return;
    const duration = mobile ? SLIDE_TOTAL_MS : FLIP_TOTAL_MS;
    timerRef.current = setTimeout(() => {
      onAnimationEndRef.current?.();
      timerRef.current = null;
    }, duration);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [direction, armed, mobile]); // duration differs slide vs flip

  // Base slot: at idle and through staging it shows the LIVE current page; from
  // one frame after arming (hidden behind the fold) it holds the frozen target
  // snapshot, so the upcoming widget tree is fully mounted and settled when the
  // fold ends and the base becomes visible again — no end-of-flip remount blink.
  const baseContent = direction && baseSwapped ? upcomingContent : currentContent;

  return (
    <FlipViewportAnimationSuppressContext.Provider value={!!direction}>
      <div ref={viewportRef} className={cx(styles.flipViewport, {
        [styles.slideViewport]: mobile && direction,
        [styles.flipArmed]:     armed,
      })}>
        <FlipSlotContext.Provider value={direction && baseSwapped ? 'upcoming' : 'idle'}>
          <div className={cx(styles.baseSlot, {
            // staging: still the visible live page — only block interaction
            [styles.baseSlotStaging]: !!direction && !armed,
            // armed: hidden behind the fold/slide overlays
            [styles.baseSlotInert]: !!direction && armed,
            [styles.baseSlotHiddenMobileSlide]: !!(mobile && direction && armed),
          })}>
            {baseContent}
          </div>
        </FlipSlotContext.Provider>

        {direction && !mobile && (
          <FlipSlotContext.Provider value="fold">
            <div className={cx(styles.flipSlot, foldSlotClass('upcomingB', direction))}>
              {upcomingContent}
            </div>
            <div className={cx(styles.flipSlot, foldSlotClass('upcomingA', direction))}>
              {upcomingContent}
            </div>
            <div className={cx(styles.flipSlot, foldSlotClass('currentA', direction))}>
              {currentContent}
            </div>
            <div className={cx(styles.flipSlot, foldSlotClass('currentB', direction))}>
              {currentContent}
            </div>
          </FlipSlotContext.Provider>
        )}

        {direction && mobile && (
          <FlipSlotContext.Provider value="fold">
            <div className={cx(styles.slideSlot, slideSlotClass('current', direction))}>
              {currentContent}
            </div>
            <div className={cx(styles.slideSlot, slideSlotClass('upcoming', direction))}>
              {upcomingContent}
            </div>
          </FlipSlotContext.Provider>
        )}
      </div>
    </FlipViewportAnimationSuppressContext.Provider>
  );
};
